import { CommonModule } from '@angular/common'
import { Component, Input } from '@angular/core'
import { Router } from '@angular/router'
import { NavFavoritesService } from '@employer/app/services/nav-favorites.service'
import { EmployerNavigationService } from '@employer/app/services/navigation.service'
import { GatedSettingsCategory, SettingsGroup } from '@employer/app/services/settings-catalog'
import { E11CardModule } from '@engineering11/ui-lib/e11-card'
import { LetDirective } from '@ngrx/component'
import { TranslateModule } from '@ngx-translate/core'
import { BehaviorSubject, Observable, combineLatest, map, of, switchMap } from 'rxjs'

/**
 * The Settings tab on the Openings and Engage screens: the SETTINGS_CATALOG entries
 * for that group, rendered as the same cards the All Company Settings hub uses.
 *
 * Deliberately a shortcut, not a second copy of the forms — every setting still has
 * exactly one home under /settings, and starring a card writes the same
 * `employer_nav_favorites` list the left-nav and hub stars write.
 */
@Component({
  selector: 'settings-group-hub',
  standalone: true,
  imports: [CommonModule, E11CardModule, LetDirective, TranslateModule],
  template: `
    <ng-container *ngrxLet="categories$ as categories">
      <ng-container *ngrxLet="favorites$ as favorites">
        <p class="e11-text-sm e11-text-skin-light e11-mb-4">
          {{
            'These live in Company Settings. This tab is a shortcut to the ones that apply here — each card opens its page there.'
              | translate
          }}
        </p>

        <div *ngIf="categories.length > 0; else noneVisible" class="hub-grid">
          <e11-card
            *ngFor="let category of categories; trackBy: trackById"
            containerClass="e11-p-4 e11-h-full e11-bg-skin-white e11-cursor-pointer hover:e11-shadow-md e11-transition-shadow"
            (click)="onSelect(category)"
          >
            <div class="e11-relative e11-flex e11-items-start e11-gap-3">
              <span
                class="hub-tile e11-w-[30px] e11-h-[30px] e11-shrink-0 e11-rounded-lg e11-flex e11-items-center e11-justify-center e11-text-skin-primary-accent"
              >
                <span class="material-symbols-outlined md-18">{{ category.icon }}</span>
              </span>
              <div class="e11-min-w-0 e11-flex-1 e11-pr-7">
                <p class="e11-font-primary-medium e11-mb-0.5">{{ category.label | translate }}</p>
                <p class="e11-text-xs e11-text-skin-light e11-mb-0">{{ category.description | translate }}</p>
              </div>
              <button
                type="button"
                class="hub-star e11-absolute e11-top-0 e11-right-0 e11-w-8 e11-h-8 e11-flex e11-items-center e11-justify-center e11-rounded e11-cursor-pointer e11-bg-transparent e11-border-0 focus-visible:e11-outline focus-visible:e11-outline-2 focus-visible:e11-outline-skin-primary-accent"
                [attr.aria-pressed]="isFavorite(favorites, category)"
                [attr.aria-label]="(isFavorite(favorites, category) ? 'Remove from favorites' : 'Add to favorites') | translate"
                [title]="(isFavorite(favorites, category) ? 'Remove from favorites' : 'Add to favorites') | translate"
                (click)="onToggleFavorite($event, category)"
              >
                <span class="material-symbols-outlined md-20" [class.star-filled]="isFavorite(favorites, category)">star</span>
              </button>
            </div>
          </e11-card>
        </div>

        <ng-template #noneVisible>
          <p class="e11-text-sm e11-text-skin-light">
            {{ 'You do not have access to any settings in this section.' | translate }}
          </p>
        </ng-template>
      </ng-container>
    </ng-container>
  `,
  styles: [
    `
      /* Same sizing rule as the All Company Settings grid: cards size themselves so a
         long label like "Job & Application Settings" stays on one line, and the track
         count falls to one as the pane narrows. */
      .hub-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 1rem;
        max-width: 64rem;
      }
      @media (max-width: 767px) {
        .hub-grid {
          gap: 0.5rem;
        }
      }

      .hub-tile {
        background: rgba(var(--color-primary-accent), 0.09);
        transition: background 0.18s ease;
      }

      .hub-star {
        color: rgb(var(--color-grey));
      }
      .hub-star:hover {
        color: rgb(var(--color-primary-accent));
      }
      .star-filled {
        font-variation-settings: 'FILL' 1;
        color: rgb(var(--color-primary-accent));
      }

      @media (prefers-reduced-motion: reduce) {
        .hub-tile {
          transition: none;
        }
      }
    `,
  ],
})
export class SettingsGroupHubComponent {
  @Input({ required: true }) set group(value: SettingsGroup) {
    this.groupSubject.next(value)
  }

  /**
   * Narrow the group to specific catalog ids. Campaigns needs exactly one card out of the Company
   * group (Marketing Channels) rather than the whole of it — and a second copy of this card, with
   * its tile, star and permission gate, is how two settings cards start looking different.
   */
  @Input() set ids(value: string[] | undefined) {
    this.idsSubject.next(value?.length ? value : undefined)
  }

  private readonly groupSubject = new BehaviorSubject<SettingsGroup>('Pipelines')
  private readonly idsSubject = new BehaviorSubject<string[] | undefined>(undefined)

  /** Only entries the user can actually reach — the same gate the /settings hub applies. */
  readonly categories$: Observable<GatedSettingsCategory[]> = combineLatest([this.groupSubject, this.idsSubject]).pipe(
    map(([group, ids]) =>
      this.navigationService.settingsCategories.filter(
        category => category.group === group && (!ids || ids.includes(category.id))
      )
    ),
    switchMap(categories =>
      categories.length === 0
        ? of([] as GatedSettingsCategory[])
        : combineLatest(categories.map(category => category.isVisible$)).pipe(
            map(flags => categories.filter((_, index) => flags[index]))
          )
    )
  )

  readonly favorites$ = this.navFavoritesService.favorites$

  constructor(
    private router: Router,
    private navigationService: EmployerNavigationService,
    private navFavoritesService: NavFavoritesService
  ) {}

  trackById = (_: number, category: GatedSettingsCategory) => category.id

  isFavorite(favorites: string[] | null, category: GatedSettingsCategory): boolean {
    return !!favorites?.includes(category.favoriteId)
  }

  onSelect(category: GatedSettingsCategory) {
    this.router.navigateByUrl(category.route)
  }

  onToggleFavorite(event: Event, category: GatedSettingsCategory) {
    event.preventDefault()
    event.stopPropagation()
    this.navFavoritesService.toggleFavorite(category.favoriteId)
  }
}
